// Which ladders wear a club's brand, and which season artwork belongs to which slug.
// Colours are not here: they are Tailwind tokens (sanur-petrol, sanur-orange, sanur-ice) in
// src/index.css, so no component ever spells out a hex value.

export type LadderBrand = {
  /** Series prefix. A slug matches when it equals the prefix or starts with `${prefix}-`. */
  seriesPrefix: string;
  organiserName: string;
  /** Transparent club logo (white on alpha), shown small in the header band. Null when the club has none. */
  logoSrc: string | null;
  /** Season artwork keyed by exact slug. Each season's art has its season set into it. */
  wordmarkBySlug: Record<string, string>;
  /** Every season of the series, oldest first. */
  seasons: Array<{ slug: string; label: string }>;
};

export const LADDER_BRANDS: LadderBrand[] = [
  {
    seriesPrefix: "sanur-showdown",
    organiserName: "Jungle Padel Sanur",
    logoSrc: "/brand/sanur-showdown/jungle-padel-sanur.svg",
    wordmarkBySlug: { "sanur-showdown-s2": "/brand/sanur-showdown/wordmark.svg" },
    seasons: [{ slug: "sanur-showdown", label: "Season 1" }, { slug: "sanur-showdown-s2", label: "Season 2" }],
  },
];

/** The brand a ladder belongs to, or null for an unbranded ladder. */
export function brandForSlug(slug: string): LadderBrand | null {
  return (
    LADDER_BRANDS.find((b) => slug === b.seriesPrefix || slug.startsWith(`${b.seriesPrefix}-`)) ?? null
  );
}

/** Share-preview image (og:image) for a ladder, site-relative. The register and season pages both read this
 *  so one season never advertises itself with another season's art. */
const SHARE_IMAGES: Record<string, string> = { "sanur-showdown-s2": "/brand/sanur-showdown/share.jpg" };
const DEFAULT_SHARE_IMAGE = "/og-banner.jpg";
export const shareImageForSlug = (slug: string): string => SHARE_IMAGES[slug] ?? DEFAULT_SHARE_IMAGE;

/** This exact season's wordmark, or null. A season without its own art is named in type instead. */
export function wordmarkForSlug(slug: string): string | null {
  return brandForSlug(slug)?.wordmarkBySlug[slug] ?? null;
}

/** The other seasons of the same series, newest first. `later` is true for a season after this one. */
export function otherSeasons(slug: string): Array<{ slug: string; label: string; later: boolean }> {
  const seasons = brandForSlug(slug)?.seasons ?? [];
  const at = seasons.findIndex((s) => s.slug === slug);
  if (at === -1) return [];
  return seasons.map((s, i) => ({ ...s, later: i > at })).filter((s) => s.slug !== slug).reverse();
}

/** "Sanur Showdown S2" is season 2; a name with no season suffix is season 1. */
export function seasonNumberFromName(name: string): number {
  const m = name.match(/\bS(?:eason)?\s*(\d+)$/i);
  return m ? Number(m[1]) : 1;
}
